import { styled } from '@automata/mui-theme';
import { ReactNode } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import useTooltipContext from './useTooltipContext';

const multi = 1.9;
const indicatorSize = 7;
const offset = multi * indicatorSize;

const OuterWrap = styled('div')<{ maxWidth?: number; shouldShift?: boolean }>`
  position: relative;
  max-width: ${(p) => p.maxWidth}px;
  width: ${(p) => (p.maxWidth ? `${p.maxWidth}px` : 'auto')};
  transform: translateX(${offset}px) translateY(-${offset}px);
  &.flipped-x {
    transform: translateX(calc(-100% - ${offset}px))
      translateY(${(p) => (p.shouldShift ? `${offset}px` : `-${offset}px`)});
  }
  &.flipped-y {
    transform: translateX(${offset}px) translateY(calc(-100% + ${offset}px));
  }
  &.flipped-y.flipped-x {
    transform: translateX(calc(-100% - ${offset}px))
      translateY(calc(-100% + ${offset}px));
  }
`;

const DropShadow = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: ${(p) => p.theme.shape.borderRadius}px;
  box-shadow: ${(p) => p.theme.shadows[10]};
`;

const ContentWrap = styled('div')<{ replan: boolean }>`
  position: relative;
  padding: ${(p) => p.theme.spacing(2)};
  border-radius: ${(p) => p.theme.shape.borderRadius}px;
  background-color: ${(p) =>
    p.replan ? p.theme.palette.background.paper : p.theme.palette.accent2[400]};
  color: ${(p) => p.theme.palette.text.primary};
`;

const Indicator = styled('div')<{ replan: boolean }>`
  position: absolute;
  top: ${indicatorSize}px;
  left: -${offset}px;
  width: 0px;
  height: 0px;
  border-style: solid;
  border-width: ${[indicatorSize, indicatorSize * 2, indicatorSize, 0].join(
    'px '
  )};
  border-color: transparent
    ${(p) =>
      p.replan
        ? p.theme.palette.background.paper
        : p.theme.palette.accent2[400]}
    transparent transparent;
  &.flipped-x {
    left: unset;
    right: -${offset}px;
    transform: rotate(180deg);
  }
  &.flipped-y {
    top: unset;
    bottom: ${indicatorSize}px;
  }
`;

export type Props = {
  children: ReactNode;
  svgHeight: number;
  svgWidth: number;
  replan?: boolean;
  maxWidth?: number;
};

const TooltipBase = ({
  children,
  svgHeight,
  svgWidth,
  replan = false,
  maxWidth,
}: Props): JSX.Element => {
  const {
    ref,
    height: tooltipHeight = 0,
    width: tooltipWidth = 0,
  } = useResizeDetector();
  const { tooltipTop = 0, tooltipLeft = 0, boundsRef } = useTooltipContext();
  const barHeight = boundsRef?.getBoundingClientRect().height || 0;

  const isFlippedY =
    tooltipTop + tooltipHeight > svgHeight && tooltipTop - tooltipHeight > 0;
  const isFlippedX = tooltipLeft + 2 * indicatorSize + tooltipWidth > svgWidth;

  const className = [
    isFlippedY ? 'flipped-y' : '',
    isFlippedX ? 'flipped-x' : '',
  ].join(' ');

  return (
    <OuterWrap
      ref={ref}
      data-testid="TooltipBase-wrap"
      className={className}
      maxWidth={maxWidth}
      shouldShift={barHeight > offset}
    >
      <DropShadow />
      <Indicator
        className={className}
        replan={replan}
        data-testid="tooltip-base-indicator"
      />
      <ContentWrap data-testid="tooltip-base-content" replan={replan}>
        {children}
      </ContentWrap>
    </OuterWrap>
  );
};

export default TooltipBase;
